import type * as api from '@gitbook/api-client';

import { subscribeToRealtimeChannel } from '../../state';
import type { StartedImportRun } from './importRuns';

/**
 * Live payloads for the runs currently being watched.
 *
 * Held once for the whole app rather than once per component: the table of contents asks about
 * imports for every page it renders, and each of those used to keep its own copy of every payload.
 */
const liveRuns = new Map<string, api.ContentImportRun>();
const liveSubscriptions = new Map<string, { count: number; unsubscribe: () => void }>();
const liveListeners = new Set<() => void>();
let liveSnapshot: ReadonlyMap<string, api.ContentImportRun> = new Map();

/**
 * Runs read back from the server for a channel that went quiet.
 *
 * Kept apart from the live payloads so the two can be reconciled: a workflow that died leaves its
 * channel replaying `in-progress` forever, and only the server knows the run has expired.
 */
const reportedRuns = new Map<string, api.ContentImportRun>();
let reportedSnapshot: ReadonlyMap<string, api.ContentImportRun> = new Map();

/**
 * Runs started in this tab.
 *
 * Only used to attribute toasts to whoever asked for the import — everything durable comes from the
 * server, so this deliberately does not survive a reload.
 */
const startedRuns: StartedImportRun[] = [];
const startedListeners = new Set<() => void>();
let startedSnapshot: StartedImportRun[] = [];

/**
 * Watch a run's channel, sharing one subscription between every caller.
 *
 * Returns a function releasing this caller's interest in it.
 */
export function retainImportRunChannel(runId: string): () => void {
    const existing = liveSubscriptions.get(runId);
    if (existing) {
        existing.count++;
    } else {
        const unsubscribe = subscribeToRealtimeChannel({ channel: 'import-run', runId }, (data) => {
            liveRuns.set(runId, data);
            refreshLiveSnapshot();
        });
        liveSubscriptions.set(runId, { count: 1, unsubscribe });
    }

    let released = false;
    return () => {
        if (released) {
            return;
        }
        released = true;

        const subscription = liveSubscriptions.get(runId);
        if (!subscription) {
            return;
        }

        subscription.count--;
        if (subscription.count > 0) {
            return;
        }

        subscription.unsubscribe();
        liveSubscriptions.delete(runId);

        // The payload stays. Callers stop watching a run once it ends, and dropping what the
        // channel last said would fall back to whatever the resource reported — which is what
        // said the run was still going, so it would look active again and be re-watched, forever.
    };
}

export function subscribeToLiveImportRuns(listener: () => void): () => void {
    liveListeners.add(listener);
    return () => {
        liveListeners.delete(listener);
    };
}

export function getLiveImportRunsSnapshot(): ReadonlyMap<string, api.ContentImportRun> {
    return liveSnapshot;
}

/**
 * Record what the server says about a run whose channel went quiet.
 */
export function setReportedImportRun(run: api.ContentImportRun): void {
    const existing = reportedRuns.get(run.id);
    if (existing && existing.status === run.status && existing.updatedAt === run.updatedAt) {
        return;
    }

    reportedRuns.set(run.id, run);
    reportedSnapshot = new Map(reportedRuns);
    liveListeners.forEach((listener) => {
        listener();
    });
}

export function getReportedImportRunsSnapshot(): ReadonlyMap<string, api.ContentImportRun> {
    return reportedSnapshot;
}

/**
 * Remember that this tab started an import.
 *
 * Kept oldest-first, so whoever needs a single one to act on picks the one that has been waiting
 * longest.
 */
export function registerStartedImportRun(run: StartedImportRun): void {
    startedRuns.push(run);
    notifyStartedImportRuns();
}

/**
 * Stop tracking a run this tab started, once there is nothing left to say about it.
 *
 * Without this the tab would hold its channel open until reload.
 */
export function forgetStartedImportRun(runId: string): void {
    const index = startedRuns.findIndex((run) => run.id === runId);
    if (index === -1) {
        return;
    }

    startedRuns.splice(index, 1);
    notifyStartedImportRuns();
}

export function subscribeToStartedImportRuns(listener: () => void): () => void {
    startedListeners.add(listener);
    return () => {
        startedListeners.delete(listener);
    };
}

export function getStartedImportRunsSnapshot(): StartedImportRun[] {
    return startedSnapshot;
}

function refreshLiveSnapshot() {
    liveSnapshot = new Map(liveRuns);
    liveListeners.forEach((listener) => {
        listener();
    });
}

function notifyStartedImportRuns() {
    startedSnapshot = [...startedRuns];
    startedListeners.forEach((listener) => {
        listener();
    });
}
